import { useState } from 'react';
import { Package } from 'lucide-react';
import { EntityAvatar } from '../ui';
import { AttachmentViewer } from './AttachmentPreview';

/**
 * THE PACK SHOT, OPENABLE.
 *
 * These thumbnails are 32-34px, which is enough to tell two products apart down a column and nowhere
 * near enough to read a label, check a pack size, or see which variant is actually in the picture. A
 * receiver counting a consignment against what is on the shelf wants to LOOK at the thing — so the
 * thumbnail opens the same full-size viewer the app uses for every other image, with the same
 * Escape and arrow-key handling (including the trick that stops one Escape closing the dialog
 * behind it as well).
 *
 * A product with no picture stays a plain avatar. A button that opens nothing is worse than no
 * button, because it has to be pressed to find that out.
 */
export function ProductThumb({ name, src, srcs, hue, size = 34 }: {
  name: string;
  /** The thumbnail. Ignored when `srcs` is given — that list's first entry is the thumbnail. */
  src?: string;
  /**
   * The product's WHOLE gallery (`productImages(p)`), when there is more than one shot of it. The
   * viewer then walks the set with the arrow keys instead of showing the first photo and hiding the
   * rest — a pack has a front, a back with the label, and often the variant line-up, and which one
   * answers "is this the right thing" depends on what the reader is checking.
   */
  srcs?: string[];
  /** Identity hue for the fallback mark — pass the product's packaging accent (`lib/productAccent`). */
  hue?: string;
  size?: number;
}) {
  const [viewAt, setViewAt] = useState<number | null>(null);
  const gallery = (srcs?.length ? srcs : src ? [src] : []).filter(Boolean);
  const thumb = gallery[0];
  const avatar = (
    <EntityAvatar name={name} src={thumb} hue={hue} square size={size} icon={<Package size={Math.round(size * 0.44)} />} />
  );
  if (!thumb) return avatar;
  return (
    <>
      <button
        type="button"
        aria-label={`View ${name}`}
        title={gallery.length > 1 ? `${name} — ${gallery.length} photos` : name}
        /* Stop the click reaching a clickable row or an expander underneath — looking at a picture
           should not also navigate somewhere. */
        onClick={(e) => { e.stopPropagation(); setViewAt(0); }}
        className="shrink-0 rounded-control transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
      >
        {avatar}
      </button>
      {viewAt != null && (
        <AttachmentViewer files={gallery} index={viewAt} label={name} onIndex={setViewAt} onClose={() => setViewAt(null)} />
      )}
    </>
  );
}
